import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  IconButton
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';
import { useLanguage } from '../../i18n/LanguageContext';
import { RoutineDay, Exercise, RoutineExercise, RoutineExerciseSet } from '../../types';
import { ExerciseAutocomplete } from '../common/ExerciseAutocomplete';
import { commonDialogStyles } from '../common/dialogStyles';

interface EditDayDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (day: RoutineDay) => void;
  day: RoutineDay;
  exercises: Exercise[];
  onCreateExercise?: (name: string) => Promise<Exercise | null>;
  setExercises?: (exercises: Exercise[]) => void;
}

export const EditDayDialog: React.FC<EditDayDialogProps> = ({
  open,
  onClose,
  onSave,
  day,
  exercises,
  onCreateExercise,
  setExercises
}) => {
  const { t } = useLanguage();
  const [dayName, setDayName] = useState('');
  const [dayNotes, setDayNotes] = useState('');
  const [routineExercises, setRoutineExercises] = useState<RoutineExercise[]>([]);
  const [expandedExercise, setExpandedExercise] = useState<{ [key: string]: boolean }>({});

  const handleCreateExercise = async (name: string): Promise<Exercise | null> => {
    if (onCreateExercise) {
      const newExercise = await onCreateExercise(name);
      if (newExercise && setExercises) {
        // Update the exercises list with the new exercise
        setExercises([...exercises, newExercise]);
      }
      return newExercise;
    }
    return null;
  };

  useEffect(() => {
    if (day) {
      setDayName(day.day_name || '');
      setDayNotes(day.notes || '');
      setRoutineExercises([...day.exercises]);
    }
  }, [day]);

  const handleClose = () => {
    onClose();
  };

  const handleSave = () => {
    const updatedDay: RoutineDay = {
      ...day,
      day_name: dayName,
      notes: dayNotes,
      exercises: routineExercises.map((exercise, index) => ({
        ...exercise,
        order_in_day: index + 1
      }))
    };
    onSave(updatedDay);
  };

  const addExercise = () => {
    const newExercise: RoutineExercise = {
      exercise_id: 0,
      order_in_day: routineExercises.length + 1,
      notes: '',
      sets: [
        {
          sets: 3,
          reps: '10',
          order_in_exercise: 1,
          notes: ''
        }
      ]
    };
    setRoutineExercises([...routineExercises, newExercise]);
  };

  const deleteExercise = (exerciseToDelete: RoutineExercise) => {
    setRoutineExercises(routineExercises.filter(ex => ex !== exerciseToDelete));
  };

  const updateExerciseId = (index: number, exerciseId: number) => {
    const selectedExercise = exercises.find(ex => ex.id === exerciseId);
    const updatedExercises = [...routineExercises];
    updatedExercises[index] = {
      ...updatedExercises[index],
      exercise_id: exerciseId,
      exercise: selectedExercise
    };
    setRoutineExercises(updatedExercises);
  };

  const updateExerciseNotes = (index: number, notes: string) => {
    const updatedExercises = [...routineExercises];
    updatedExercises[index] = {
      ...updatedExercises[index],
      notes
    };
    setRoutineExercises(updatedExercises);
  };

  const addSetToExercise = (exerciseIndex: number) => {
    const updatedExercises = [...routineExercises];
    const exercise = updatedExercises[exerciseIndex];
    const newSet: RoutineExerciseSet = {
      sets: 3,
      reps: '10',
      order_in_exercise: exercise.sets.length + 1,
      notes: ''
    };
    exercise.sets.push(newSet);
    setRoutineExercises(updatedExercises);
  };

  const deleteSetFromExercise = (exerciseIndex: number, setIndex: number) => {
    const updatedExercises = [...routineExercises];
    updatedExercises[exerciseIndex].sets.splice(setIndex, 1);
    // Reorder remaining sets
    updatedExercises[exerciseIndex].sets.forEach((set, idx) => {
      set.order_in_exercise = idx + 1;
    });
    setRoutineExercises(updatedExercises);
  };

  const updateSet = (exerciseIndex: number, setIndex: number, updatedSet: Partial<RoutineExerciseSet>) => {
    const updatedExercises = [...routineExercises];
    updatedExercises[exerciseIndex].sets[setIndex] = {
      ...updatedExercises[exerciseIndex].sets[setIndex],
      ...updatedSet
    };
    setRoutineExercises(updatedExercises);
  };

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedExercise(prev => ({
      ...prev,
      [panel]: isExpanded
    }));
  };

  const isAccordionExpanded = (index: number) => {
    const panelKey = `exercise-${index}`;
    return expandedExercise[panelKey] !== undefined ? expandedExercise[panelKey] : true; // Default to true (open)
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth sx={commonDialogStyles}>
      <DialogTitle>
        {t('routines.editDay')} - {t('routines.dayNumber')} {day.day_number}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          {/* Day Information */}
          <TextField
            label={t('routines.dayName')}
            value={dayName}
            onChange={(e) => setDayName(e.target.value)}
            fullWidth
            placeholder={t('routines.dayNamePlaceholder')}
          />

          <TextField
            label={t('routines.dayNotes')}
            value={dayNotes}
            onChange={(e) => setDayNotes(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder={t('routines.dayNotesPlaceholder')}
          />

          {/* Exercises Section */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">{t('routines.exercises')}</Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addExercise}
                size="small"
              >
                {t('routines.addExercise')}
              </Button>
            </Box>

            {routineExercises.length === 0 ? (
              <Box
                sx={{
                  p: 3,
                  textAlign: 'center',
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1
                }}
              >
                <Typography color="text.secondary">
                  {t('routines.noExercises')}
                </Typography>
              </Box>
            ) : (
              routineExercises.map((exercise, exerciseIndex) => (
                <Accordion
                  key={exerciseIndex}
                  expanded={isAccordionExpanded(exerciseIndex)}
                  onChange={handleAccordionChange(`exercise-${exerciseIndex}`)}
                  sx={{ mb: 1 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mr: 2 }}>
                      <DragIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography sx={{ flex: 1 }}>
                        {exerciseIndex + 1}. {exercise.exercise?.name || t('routines.selectExercise')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                        {exercise.sets.length} {exercise.sets.length === 1 ? 'set' : 'sets'}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteExercise(exercise);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* Exercise Selection */}
                      <ExerciseAutocomplete
                        exercises={exercises}
                        value={exercise.exercise_id ? exercises.find(ex => ex.id === exercise.exercise_id) || null : null}
                        onChange={(selectedExercise) => {
                          if (selectedExercise) {
                            updateExerciseId(exerciseIndex, selectedExercise.id);
                          } else {
                            updateExerciseId(exerciseIndex, 0);
                          }
                        }}
                        label={t('routines.selectExercise')}
                        onCreateExercise={handleCreateExercise}
                      />

                      {/* Exercise Notes */}
                      <TextField
                        label={t('routines.exerciseNotes')}
                        value={exercise.notes || ''}
                        onChange={(e) => updateExerciseNotes(exerciseIndex, e.target.value)}
                        fullWidth
                        placeholder={t('routines.exerciseNotesPlaceholder')}
                      />

                      {/* Sets Configuration */}
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="subtitle1">{t('routines.sets')}</Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => addSetToExercise(exerciseIndex)}
                          >
                            {t('routines.addSetConfig')}
                          </Button>
                        </Box>

                        {exercise.sets.map((set, setIndex) => (
                          <Paper key={setIndex} sx={{ p: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Box sx={{ flex: '1 1 120px', minWidth: '120px' }}>
                                <TextField
                                  label={t('routines.sets')}
                                  type="number"
                                  value={set.sets}
                                  onChange={(e) => updateSet(exerciseIndex, setIndex, { sets: Number(e.target.value) })}
                                  fullWidth
                                  inputProps={{ min: 1 }}
                                />
                              </Box>
                              <Box sx={{ flex: '1 1 120px', minWidth: '120px' }}>
                                <TextField
                                  label={t('routines.reps')}
                                  value={set.reps}
                                  onChange={(e) => updateSet(exerciseIndex, setIndex, { reps: e.target.value })}
                                  fullWidth
                                  placeholder={t('routines.repsPlaceholder')}
                                />
                              </Box>
                              <Box sx={{ flex: '2 1 200px', minWidth: '200px' }}>
                                <TextField
                                  label={t('routines.setNotes')}
                                  value={set.notes || ''}
                                  onChange={(e) => updateSet(exerciseIndex, setIndex, { notes: e.target.value })}
                                  fullWidth
                                  placeholder={t('routines.setNotesPlaceholder')}
                                />
                              </Box>
                              <Box>
                                <IconButton
                                  size="small"
                                  onClick={() => deleteSetFromExercise(exerciseIndex, setIndex)}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('common.cancel')}</Button>
        <Button onClick={handleSave} variant="contained">
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 